import { z } from "zod/v4";
import {
  type IdrGraphDocumentEditOperation,
  IdrGraphDocumentEditOperationSchema,
} from "./graph-apply-schema";
import { IdrGraphDocumentSchema } from "./graph-document-schema";
import { IdrGraphRevisionSchema } from "./graph-revision-schema";
import { IdrChangePlanContentSchema } from "./schema";

export const IdrGraphDocumentEditOperationsSchema = z
  .array(IdrGraphDocumentEditOperationSchema)
  .min(1)
  .superRefine((operations, ctx) => {
    const actions = new Set<IdrGraphDocumentEditOperation["action"]>();
    for (const [index, operation] of operations.entries()) {
      if (actions.has(operation.action)) {
        ctx.addIssue({
          code: "custom",
          message: `Operation "${operation.action}" appears more than once`,
          path: [index, "action"],
        });
      }
      actions.add(operation.action);
    }
  });

export const IdrGraphDocumentReviewContentSchema = z
  .object({
    summary: z.string().min(1),
    graphId: z.string().uuid(),
    baseRevision: IdrGraphRevisionSchema,
    document: IdrGraphDocumentSchema,
    operations: IdrGraphDocumentEditOperationsSchema,
  })
  .strict();

export type IdrGraphDocumentReview = z.infer<
  typeof IdrGraphDocumentReviewContentSchema
>;

export const IdrReviewContentSchema = z.union([
  IdrChangePlanContentSchema,
  IdrGraphDocumentReviewContentSchema,
]);

export type IdrReviewContent = z.infer<typeof IdrReviewContentSchema>;

const persistedReviewState = {
  appliedGraphId: z.string().nullable(),
  denied: z.boolean().optional(),
  stale: z.boolean().optional(),
};

export const IdrReviewMessageContentSchema = z
  .union([
    IdrChangePlanContentSchema.extend(persistedReviewState),
    IdrGraphDocumentReviewContentSchema.extend(persistedReviewState),
  ])
  .refine(
    ({ appliedGraphId, denied, stale }) =>
      Number(Boolean(appliedGraphId)) +
        Number(Boolean(denied)) +
        Number(Boolean(stale)) <=
      1,
    "An IDR graph plan cannot have more than one terminal state",
  );

export type IdrReviewMessageContent = z.infer<
  typeof IdrReviewMessageContentSchema
>;
